





























import {Component} from 'vue-property-decorator'
import axios from 'axios'
import {
  newApiV1AdminSessionPath,
  apiV1AuthWebauthnOptionsPath,
  apiV1AuthWebauthnVerifyPath
} from '@/rails/routes'
import VueBase from '@/pages/vue-base'
import {
  isWebAuthnSupported,
  prepareRequestOptions,
  serializeAssertionCredential,
  signalUnknownCredentialIfSupported
} from '@/utils/webauthn'

@Component
export default class Login extends VueBase {
  email = ''
  password = ''
  submitting = false
  passkeyLoading = false
  webauthnSupported = false

  mounted(): void {
    this.webauthnSupported = isWebAuthnSupported()
  }

  private saveAuthAndRedirect(headers: Record<string, string>): void {
    const auth = {
      authClient: headers.client,
      authUId: headers.uid,
      authAccessToken: headers['access-token']
    }
    localStorage.setItem('auth', JSON.stringify(auth))
    this.$router.push(`/admin/${this.settings.admin.menus[0]}`)
  }

  async login(): Promise<void> {
    if (this.submitting) return
    this.submitting = true
    try {
      const res = await axios.post(newApiV1AdminSessionPath(), {
        email: this.email,
        password: this.password
      })

      if (res?.data?.require_passkey) {
        this.showMessage(res.data.message || 'パスキーによる認証を行ってください')
        await this.loginWithPasskey(this.email, res.data)
        return
      }

      if (res?.status === 200 && res.headers['access-token']) {
        this.saveAuthAndRedirect(res.headers)
      } else {
        this.showError('ログインに失敗しました')
      }
    } catch (e) {
      let errMsg = 'ログインに失敗しました'
      try {
        const parsed = JSON.parse(e.request?.response || '{}')
        errMsg = parsed.error || (Array.isArray(parsed.errors) ? parsed.errors.join(', ') : errMsg)
      } catch {
      }
      this.showError(errMsg)
    } finally {
      this.submitting = false
    }
  }

  async loginWithPasskey(targetEmail?: string, preloadedData?: any): Promise<void> {
    if (!this.webauthnSupported) {
      this.showError('お使いのブラウザはパスキーに対応していません')
      return
    }
    if (this.passkeyLoading) return
    this.passkeyLoading = true
    let rpId = window.location.hostname
    let attemptedCredentialId = ''
    try {
      let data = preloadedData
      if (!data || !data.webauthn_options) {
        const optionsRes = await axios.post(apiV1AuthWebauthnOptionsPath(), {
          email: (targetEmail !== undefined ? targetEmail : this.email).trim()
        })
        data = optionsRes.data
      }
      const rawOptions = data.webauthn_options || data
      if (data.rp_id || rawOptions.rpId) {
        rpId = data.rp_id || rawOptions.rpId
      }
      const publicKey = prepareRequestOptions(rawOptions)
      const credential = (await navigator.credentials.get({
        publicKey
      })) as PublicKeyCredential | null

      if (!credential) {
        this.showError('パスキー認証がキャンセルされました')
        return
      }

      const serialized = serializeAssertionCredential(credential)
      attemptedCredentialId = serialized.id

      const verifyRes = await axios.post(apiV1AuthWebauthnVerifyPath(), {
        credential: serialized,
        challenge_token: data.challenge_token
      })

      if (verifyRes?.status === 200 && verifyRes.headers['access-token']) {
        this.saveAuthAndRedirect(verifyRes.headers)
      } else {
        this.showError('パスキー認証に失敗しました')
      }
    } catch (e) {
      if (e?.name === 'NotAllowedError') {
        this.showError('パスキー認証がキャンセルされたか、タイムアウトしました')
        return
      }
      const status = e?.response?.status || e?.request?.status
      if (status === 404 && attemptedCredentialId) {
        await signalUnknownCredentialIfSupported(rpId, attemptedCredentialId)
      }
      const serverErr = e?.response?.data?.error
      this.showError(serverErr || 'パスキー認証に失敗しました')
    } finally {
      this.passkeyLoading = false
    }
  }
}
